import { Box, Card, CardContent, Tooltip, Typography } from '@mui/material'
import React, { useMemo } from 'react'

import type { Appointment } from '@/features/osa-scheduling/types'

interface StageData {
  stage: string
  count: number
  color: string
}

interface StageOverviewData {
  new: {
    all: number
    old: number
  }
  dispatched: {
    all: number
    old: number
  }
  completed: number
  cancelled: number
}

interface ProjectCountOverviewProps {
  data?: StageOverviewData
  isLoading: boolean
  error: Error | null
  appointmentsData?: Appointment[]
}

const ProjectCountOverview: React.FC<ProjectCountOverviewProps> = ({
  data,
  isLoading,
  error,
  appointmentsData,
}) => {
  const { privatCount, isfpCount, marketplaceCount, otherCount } = useMemo(() => {
    if (!appointmentsData) {
      return {
        privatCount: 0,
        isfpCount: 0,
        marketplaceCount: 0,
        otherCount: 0,
      }
    }

    let privat = 0
    let isfp = 0
    let marketplace = 0
    let other = 0

    appointmentsData.forEach(apt => {
      if (apt.scoutCreated) {
        privat++
      } else if (apt.type?.toLowerCase() === 'isfp') {
        isfp++
      } else if (apt.type?.toLowerCase() === 'marketplace') {
        marketplace++
      } else {
        other++
      }
    })

    return {
      privatCount: privat,
      isfpCount: isfp,
      marketplaceCount: marketplace,
      otherCount: other,
    }
  }, [appointmentsData])

  const hasAnyAppointments = appointmentsData && appointmentsData.length > 0

  const stageData: StageData[] = [
    { stage: 'New', count: data?.new.all || 0, color: '#2196F3' },
    { stage: 'Dispatched', count: data?.dispatched.all || 0, color: '#FF9800' },
    { stage: 'Completed', count: data?.completed || 0, color: '#4CAF50' },
    { stage: 'Cancelled', count: data?.cancelled || 0, color: '#F44336' },
  ]

  const shimmerKeyframes = `
    @keyframes shimmer {
      0% { 
        transform: translateX(-100%);
      }
      100% { 
        transform: translateX(100%);
      }
    }
  `

  const numberContainerStyle = {
    height: '0.95rem',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-end',
  }

  const skeletonStyle = {
    display: 'inline-block',
    width: '24px',
    height: '0.95rem',
    backgroundColor: '#e0e0e0',
    borderRadius: '4px',
    position: 'relative' as const,
    overflow: 'hidden',
    '&::after': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      background:
        'linear-gradient(90deg, transparent 0%, rgba(255,255,255,0.8) 50%, transparent 100%)',
      animation: 'shimmer 1.5s infinite',
    },
  }

  if (error) {
    return (
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardContent sx={{ p: 3, flexGrow: 1 }}>
          <Typography color="error">Failed to load data</Typography>
        </CardContent>
      </Card>
    )
  }

  return (
    <>
      <style>{shimmerKeyframes}</style>
      <Card
        sx={{
          boxShadow: 2,
          borderRadius: 2,
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <CardContent sx={{ p: 3, flexGrow: 1 }}>
          <Typography
            variant="h6"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              mb: 1,
              fontSize: '1rem',
            }}
          >
            Projects by Stage
          </Typography>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75 }}>
            {stageData.map(item => (
              <Box
                key={item.stage}
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  py: 0.25,
                  minWidth: '200px',
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: item.color,
                    fontWeight: 500,
                    fontSize: '0.875rem',
                  }}
                >
                  {item.stage}:
                </Typography>
                <Box sx={numberContainerStyle}>
                  {(() => {
                    if (isLoading) {
                      return <Box sx={skeletonStyle} />
                    }

                    if (item.stage === 'Dispatched' && appointmentsData && hasAnyAppointments) {
                      return (
                        <Tooltip
                          title={
                            <Box>
                              {privatCount > 0 && (
                                <Typography variant="body2">Privat: {privatCount}</Typography>
                              )}
                              {isfpCount > 0 && (
                                <Typography variant="body2">iSFP: {isfpCount}</Typography>
                              )}
                              {marketplaceCount > 0 && (
                                <Typography variant="body2">
                                  Marketplace: {marketplaceCount}
                                </Typography>
                              )}
                              {otherCount > 0 && (
                                <Typography variant="body2">Other: {otherCount}</Typography>
                              )}
                            </Box>
                          }
                          arrow
                          placement="left"
                        >
                          <Typography
                            variant="body2"
                            sx={{
                              fontWeight: 600,
                              color: 'text.primary',
                              fontSize: '0.95rem',
                              cursor: 'help',
                              textDecoration: 'underline',
                              textDecorationStyle: 'dotted',
                              textUnderlineOffset: '2px',
                            }}
                          >
                            {item.count}
                          </Typography>
                        </Tooltip>
                      )
                    }

                    return (
                      <Typography
                        variant="body2"
                        sx={{
                          fontWeight: 600,
                          color: 'text.primary',
                          fontSize: '0.95rem',
                        }}
                      >
                        {item.count}
                      </Typography>
                    )
                  })()}
                </Box>
              </Box>
            ))}
          </Box>
        </CardContent>
      </Card>
    </>
  )
}

export default ProjectCountOverview
